import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/api';
import './EmployeeDossierPanel.css';

type DossierRecord = {
  employee: {
    id: string;
    userName: string;
    firstName: string;
    lastName: string;
    position: string;
    hireDate?: string;
    departmentName?: string;
    isActive: boolean;
    useFixedRateSalary: boolean;
    fixedMonthlyRate: number;
    revenuePercent: number;
  };
  characteristic: string;
  hasPhoto: boolean;
  targetYear: number;
  targetMonth: number;
  attendanceHours: number;
  attendance: Array<{
    workDate: string;
    checkInAt: string;
    checkOutAt?: string;
    hoursWorked: number;
    hourCoefficient: number;
    comment: string;
  }>;
  salary: {
    method: string;
    fixedMonthlyRate: number;
    revenuePercent: number;
    evaluationCoefficient?: number | null;
    evaluationIssue?: string | null;
    monthlyRevenue: number;
    estimate?: number | null;
    hallsPayroll?: {
      departmentNameSnapshot: string;
      status: string;
      workedHours: number;
      evaluationCoefficient: number;
      amount: number;
      adjustments: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
    } | null;
    monthlyPayroll?: {
      status: string;
      calculationMethod: string;
      workedHours: number;
      hourlyRate: number;
      evaluationCoefficient: number;
      amount: number;
      adjustments: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
    } | null;
  };
  medicalChecks: Array<{
    id: number;
    examDate: string;
    nextDueDate?: string;
    result: string;
    notes: string;
    createdByUsername: string;
  }>;
  issuedItems: Array<{
    id: number;
    itemName: string;
    quantity: number;
    unit: string;
    issuedOn: string;
    expectedReturnOn?: string;
    returnedOn?: string;
    conditionNotes: string;
    notes: string;
    createdByUsername: string;
  }>;
};

const today = () => new Date().toISOString().slice(0, 10);
const createIdempotencyKey = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
);
const dateLabel = (value?: string) => value ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString('uk-UA') : '—';
const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character] ?? character));
const money = (amount?: number | null) => amount == null
  ? 'Не сформовано'
  : `${new Intl.NumberFormat('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ₴`;

const EmployeeDossierPanel: React.FC<{ appUserId: string }> = ({ appUserId }) => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [record, setRecord] = useState<DossierRecord | null>(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [characteristic, setCharacteristic] = useState('');
  const [examDate, setExamDate] = useState(today());
  const [nextDueDate, setNextDueDate] = useState('');
  const [examResult, setExamResult] = useState('');
  const [examNotes, setExamNotes] = useState('');
  const [examIdempotencyKey, setExamIdempotencyKey] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemQuantity, setItemQuantity] = useState('1');
  const [itemUnit, setItemUnit] = useState('шт.');
  const [issuedOn, setIssuedOn] = useState(today());
  const [expectedReturnOn, setExpectedReturnOn] = useState('');
  const [conditionNotes, setConditionNotes] = useState('');
  const [itemNotes, setItemNotes] = useState('');
  const [itemIdempotencyKey, setItemIdempotencyKey] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    const [year, monthNumber] = month.split('-').map(Number);
    const accessResponse = await api.get<{ permissions: string[]; editPermissions: string[] }>('/users/me/access');
    if (!accessResponse.data.permissions.includes('page.management.employee-dossiers')) {
      setCanEdit(false);
      setRecord(null);
      setError('Для перегляду особового досьє потрібен окремий дозвіл HR.');
      return;
    }
    const dossierResponse = await api.get<DossierRecord>(
      `/employee-dossiers/${encodeURIComponent(appUserId)}`,
      { params: { year, month: monthNumber } },
    );
    const nextRecord = dossierResponse.data;
    setRecord(nextRecord);
    setCharacteristic(nextRecord.characteristic);
    setCanEdit(
      accessResponse.data.permissions.includes('page.management.employee-dossiers') &&
      accessResponse.data.editPermissions.includes('page.management.employee-dossiers'),
    );
    if (nextRecord.hasPhoto) {
      const photo = await api.get<Blob>(`/employee-dossiers/${encodeURIComponent(appUserId)}/photo`, { responseType: 'blob' });
      const nextUrl = URL.createObjectURL(photo.data);
      setPhotoUrl(current => {
        if (current) URL.revokeObjectURL(current);
        return nextUrl;
      });
    } else {
      setPhotoUrl(current => {
        if (current) URL.revokeObjectURL(current);
        return '';
      });
    }
  }, [appUserId, month]);

  useEffect(() => {
    let active = true;
    setError('');
    void load().catch(() => {
      if (active) setError('Немає доступу до особового досьє або не вдалося його завантажити.');
    });
    return () => {
      active = false;
      setPhotoUrl(current => {
        if (current) URL.revokeObjectURL(current);
        return '';
      });
    };
  }, [load]);

  const perform = async (action: () => Promise<unknown>, success: string, onSuccess?: () => void) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      await load();
      onSuccess?.();
      setNotice(success);
    } catch (actionError) {
      const message = typeof actionError === 'object' && actionError !== null && 'response' in actionError
        ? (actionError as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      setError(message || 'Не вдалося зберегти дані досьє.');
    } finally {
      setBusy(false);
    }
  };

  const savePhoto = () => {
    if (!photoFile) return;
    const body = new FormData();
    body.append('file', photoFile);
    void perform(async () => {
      await api.put(`/employee-dossiers/${encodeURIComponent(appUserId)}/photo`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoFile(null);
    }, 'Фото працівника збережено приватно.');
  };

  const printDossier = () => {
    if (!record) return;
    const employee = record.employee;
    const attendanceRows = record.attendance.map(item =>
      `<tr><td>${dateLabel(item.workDate)}</td><td>${escapeHtml(item.checkInAt.slice(11, 16))}</td><td>${escapeHtml(item.checkOutAt?.slice(11, 16) || '—')}</td><td>${item.hoursWorked.toFixed(2)}</td><td>${item.hourCoefficient.toFixed(2)}</td><td>${escapeHtml(item.comment)}</td></tr>`,
    ).join('');
    const medicalRows = record.medicalChecks.map(item =>
      `<tr><td>${dateLabel(item.examDate)}</td><td>${dateLabel(item.nextDueDate)}</td><td>${escapeHtml(item.result)}</td><td>${escapeHtml(item.notes)}</td><td>${escapeHtml(item.createdByUsername)}</td></tr>`,
    ).join('');
    const itemRows = record.issuedItems.map(item =>
      `<tr><td>${escapeHtml(item.itemName)}</td><td>${item.quantity} ${escapeHtml(item.unit)}</td><td>${dateLabel(item.issuedOn)}</td><td>${dateLabel(item.expectedReturnOn)}</td><td>${dateLabel(item.returnedOn)}</td><td>${escapeHtml(item.conditionNotes)}</td><td>${escapeHtml(item.notes)}</td></tr>`,
    ).join('');
    const payroll = record.salary.hallsPayroll;
    const monthlyPayroll = record.salary.monthlyPayroll;
    const photo = photoUrl ? `<img class="photo" src="${escapeHtml(photoUrl)}" alt="Фото працівника">` : '';
    const html = `<!doctype html><html lang="uk"><head><meta charset="utf-8"><title>Досьє — ${escapeHtml(employee.firstName)} ${escapeHtml(employee.lastName)}</title>
      <style>
      body{font:12px Arial,sans-serif;color:#17212b;margin:24px}h1{font-size:21px;margin:0 0 14px}h2{font-size:15px;border-bottom:1px solid #88939e;padding-bottom:4px;margin:18px 0 8px}
      .top{display:flex;gap:18px;align-items:flex-start}.photo{width:110px;height:135px;object-fit:cover;border:1px solid #89939c}
      .facts{display:grid;grid-template-columns:1fr 1fr;gap:5px}.facts p{margin:2px 0}table{width:100%;border-collapse:collapse;margin:6px 0 12px}th,td{border:1px solid #9aa3aa;padding:4px;text-align:left;vertical-align:top}th{background:#eaf0f4}
      .muted{color:#515c65;white-space:pre-wrap}.notice{font-size:10px;margin-top:18px}
      @page{size:A4 landscape;margin:12mm}@media print{body{margin:0}}
      </style></head><body><h1>Особове досьє працівника</h1><div class="top">${photo}<div class="facts">
      <p><b>ПІБ:</b> ${escapeHtml(`${employee.firstName} ${employee.lastName}`.trim())}</p><p><b>Статус:</b> ${employee.isActive ? 'Активний' : 'Неактивний'}</p>
      <p><b>Посада:</b> ${escapeHtml(employee.position)}</p><p><b>Підрозділ:</b> ${escapeHtml(employee.departmentName || '—')}</p>
      <p><b>Дата прийняття:</b> ${dateLabel(employee.hireDate)}</p><p><b>Період звіту:</b> ${record.targetMonth}.${record.targetYear}</p></div></div>
      <h2>Особиста характеристика</h2><div class="muted">${escapeHtml(record.characteristic || 'Не заповнено')}</div>
      <h2>Зарплата та нарахування за період</h2><p>Метод: ${employee.useFixedRateSalary ? 'Ставка + відсоток від місячного виторгу' : 'За коефіцієнтом / розподілом підрозділу'}. Ставка: ${money(record.salary.fixedMonthlyRate)}. Відсоток: ${record.salary.revenuePercent.toFixed(2)}%. Місячний виторг: ${money(record.salary.monthlyRevenue)}. Поточний коефіцієнт: ${record.salary.evaluationCoefficient == null ? escapeHtml(record.salary.evaluationIssue || 'не визначено') : record.salary.evaluationCoefficient.toFixed(4)}. Розрахункова сума: ${money(record.salary.estimate)}.</p>
      <p>Весільні зали — статус ${escapeHtml(payroll?.status || 'не сформовано')}; ${payroll ? `години ${payroll.workedHours.toFixed(2)}, коефіцієнт ${payroll.evaluationCoefficient.toFixed(4)}, нараховано ${money(payroll.amount)}` : 'окремого зафіксованого нарахування за цей місяць немає'}.</p>
      ${payroll?.adjustments.length ? `<p>Коригування: ${payroll.adjustments.map(item => `${money(item.amount)} — ${escapeHtml(item.reason)} (${escapeHtml(item.createdByUsername)})`).join('; ')}</p>` : ''}
      <p>Загальний зарплатний період — статус ${escapeHtml(monthlyPayroll?.status || 'не сформовано')}; ${monthlyPayroll ? `метод ${escapeHtml(monthlyPayroll.calculationMethod)}, години ${monthlyPayroll.workedHours.toFixed(2)}, погодинна ставка ${money(monthlyPayroll.hourlyRate)}, коефіцієнт ${monthlyPayroll.evaluationCoefficient.toFixed(4)}, нараховано ${money(monthlyPayroll.amount)}` : `поточна попередня оцінка ${money(record.salary.estimate)}`}.</p>
      ${monthlyPayroll?.adjustments.length ? `<p>Коригування: ${monthlyPayroll.adjustments.map(item => `${money(item.amount)} — ${escapeHtml(item.reason)} (${escapeHtml(item.createdByUsername)})`).join('; ')}</p>` : ''}
      <h2>Табелювання за місяць (${record.attendanceHours.toFixed(2)} год)</h2><table><thead><tr><th>Дата</th><th>Прихід</th><th>Вихід</th><th>Години</th><th>Коеф.</th><th>Примітка</th></tr></thead><tbody>${attendanceRows || '<tr><td colspan="6">Записів немає</td></tr>'}</tbody></table>
      <h2>Медичні огляди</h2><table><thead><tr><th>Дата</th><th>Наступний до</th><th>Результат</th><th>Примітки</th><th>Вніс</th></tr></thead><tbody>${medicalRows || '<tr><td colspan="5">Записів немає</td></tr>'}</tbody></table>
      <h2>Видані речі та інвентар (обліковий журнал; не змінює складські залишки)</h2><table><thead><tr><th>Річ</th><th>Кількість</th><th>Видано</th><th>Повернути до</th><th>Повернуто</th><th>Стан</th><th>Примітка</th></tr></thead><tbody>${itemRows || '<tr><td colspan="7">Записів немає</td></tr>'}</tbody></table>
      <p class="notice">Містить конфіденційні персональні дані. Поширювати лише уповноваженим особам.</p></body></html>`;
    const frame = document.createElement('iframe');
    frame.title = 'Попередній перегляд досьє для друку';
    frame.style.position = 'fixed';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.srcdoc = html;
    frame.onload = () => {
      const printWindow = frame.contentWindow;
      if (!printWindow) {
        frame.remove();
        setError('Не вдалося підготувати досьє до друку.');
        return;
      }
      printWindow.addEventListener('afterprint', () => frame.remove(), { once: true });
      printWindow.focus();
      printWindow.print();
    };
    document.body.appendChild(frame);
  };

  const [yearText, monthText] = month.split('-');
  return (
    <section className="employee-dossier">
      <header className="employee-dossier__header">
        <div><h3>Особове досьє</h3><p>Приватні медичні дані та фото доступні лише уповноваженим користувачам.</p></div>
        <label>Місяць звіту<input aria-label="Місяць досьє" type="month" value={month} onChange={event => setMonth(event.target.value)} /></label>
      </header>
      {error && <p role="alert" className="employee-dossier__error">{error}</p>}
      {notice && <p role="status" className="employee-dossier__notice">{notice}</p>}
      {!record ? <p>Завантаження досьє…</p> : (
        <>
          <section className="employee-dossier__card">
            <h4>Особиста характеристика</h4>
            <textarea aria-label="Особиста характеристика" value={characteristic} maxLength={8000} onChange={event => setCharacteristic(event.target.value)} disabled={!canEdit || busy} />
            {canEdit && <button type="button" disabled={busy} onClick={() => void perform(
              () => api.put(`/employee-dossiers/${encodeURIComponent(appUserId)}/characteristic`, { characteristic }),
              'Характеристику збережено.',
            )}>Зберегти характеристику</button>}
          </section>

          <section className="employee-dossier__card">
            <h4>Фото працівника</h4>
            {photoUrl && <img className="employee-dossier__photo" src={photoUrl} alt="Фото працівника" />}
            {canEdit && <div className="employee-dossier__form">
              <label>Фото (JPEG, PNG, WEBP, GIF або BMP; до 30 МБ)<input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/bmp" onChange={event => setPhotoFile(event.target.files?.[0] ?? null)} /></label>
              <button type="button" disabled={busy || !photoFile} onClick={savePhoto}>Завантажити фото</button>
              {record.hasPhoto && <button type="button" disabled={busy} onClick={() => void perform(
                () => api.delete(`/employee-dossiers/${encodeURIComponent(appUserId)}/photo`),
                'Фото видалено.',
              )}>Видалити фото</button>}
            </div>}
          </section>

          <section className="employee-dossier__card">
            <h4>Медичні огляди</h4>
            {canEdit && <form className="employee-dossier__form" onSubmit={event => {
              event.preventDefault();
              void perform(async () => {
                const idempotencyKey = examIdempotencyKey || createIdempotencyKey();
                setExamIdempotencyKey(idempotencyKey);
                await api.post(`/employee-dossiers/${encodeURIComponent(appUserId)}/medical-checks`, {
                  examDate, nextDueDate: nextDueDate || null, result: examResult, notes: examNotes,
                }, { headers: { 'Idempotency-Key': idempotencyKey } });
              }, 'Запис про медогляд додано.', () => {
                setExamResult(''); setExamNotes(''); setNextDueDate('');
                setExamIdempotencyKey('');
              });
            }}>
              <label>Дата огляду<input type="date" required value={examDate} onChange={event => { setExamDate(event.target.value); setExamIdempotencyKey(''); }} /></label>
              <label>Наступний огляд до<input type="date" value={nextDueDate} onChange={event => { setNextDueDate(event.target.value); setExamIdempotencyKey(''); }} /></label>
              <label>Результат<input required maxLength={200} value={examResult} onChange={event => { setExamResult(event.target.value); setExamIdempotencyKey(''); }} /></label>
              <label>Примітка<input maxLength={2000} value={examNotes} onChange={event => { setExamNotes(event.target.value); setExamIdempotencyKey(''); }} /></label>
              <button type="submit" disabled={busy}>Додати запис</button>
            </form>}
            <div className="employee-dossier__table-wrap"><table><thead><tr><th>Дата огляду</th><th>Наступний до</th><th>Результат</th><th>Примітки</th></tr></thead><tbody>
              {record.medicalChecks.map(check => <tr key={check.id}><td>{dateLabel(check.examDate)}</td><td>{dateLabel(check.nextDueDate)}</td><td>{check.result}</td><td>{check.notes || '—'}</td></tr>)}
              {!record.medicalChecks.length && <tr><td colSpan={4}>Записів немає.</td></tr>}
            </tbody></table></div>
          </section>

          <section className="employee-dossier__card">
            <h4>Видані речі та інвентар</h4>
            <p>Це кадровий журнал відповідальності, він не проводить складське списання і не змінює залишки.</p>
            {canEdit && <form className="employee-dossier__form" onSubmit={event => {
              event.preventDefault();
              void perform(async () => {
                const idempotencyKey = itemIdempotencyKey || createIdempotencyKey();
                setItemIdempotencyKey(idempotencyKey);
                await api.post(`/employee-dossiers/${encodeURIComponent(appUserId)}/issued-items`, {
                  itemName, quantity: Number(itemQuantity), unit: itemUnit, issuedOn,
                  expectedReturnOn: expectedReturnOn || null, conditionNotes, notes: itemNotes,
                }, { headers: { 'Idempotency-Key': idempotencyKey } });
              }, 'Видачу записано в досьє.', () => {
                setItemName(''); setItemQuantity('1'); setExpectedReturnOn(''); setConditionNotes(''); setItemNotes('');
                setItemIdempotencyKey('');
              });
            }}>
              <label>Річ / інвентар<input required maxLength={200} value={itemName} onChange={event => { setItemName(event.target.value); setItemIdempotencyKey(''); }} /></label>
              <label>Кількість<input required type="number" min="0.001" step="0.001" value={itemQuantity} onChange={event => { setItemQuantity(event.target.value); setItemIdempotencyKey(''); }} /></label>
              <label>Одиниця<input required maxLength={40} value={itemUnit} onChange={event => { setItemUnit(event.target.value); setItemIdempotencyKey(''); }} /></label>
              <label>Видано<input required type="date" value={issuedOn} onChange={event => { setIssuedOn(event.target.value); setItemIdempotencyKey(''); }} /></label>
              <label>Очікуване повернення<input type="date" value={expectedReturnOn} onChange={event => { setExpectedReturnOn(event.target.value); setItemIdempotencyKey(''); }} /></label>
              <label>Стан при видачі<input maxLength={1000} value={conditionNotes} onChange={event => { setConditionNotes(event.target.value); setItemIdempotencyKey(''); }} /></label>
              <label>Примітка<input maxLength={2000} value={itemNotes} onChange={event => { setItemNotes(event.target.value); setItemIdempotencyKey(''); }} /></label>
              <button type="submit" disabled={busy}>Зафіксувати видачу</button>
            </form>}
            <div className="employee-dossier__table-wrap"><table><thead><tr><th>Річ</th><th>Кількість</th><th>Видано</th><th>Повернути до</th><th>Повернуто</th><th>Стан / примітка</th><th>Дія</th></tr></thead><tbody>
              {record.issuedItems.map(item => <tr key={item.id}>
                <td>{item.itemName}</td><td>{item.quantity} {item.unit}</td><td>{dateLabel(item.issuedOn)}</td><td>{dateLabel(item.expectedReturnOn)}</td><td>{dateLabel(item.returnedOn)}</td>
                <td>{[item.conditionNotes, item.notes].filter(Boolean).join(' · ') || '—'}</td>
                <td>{canEdit && !item.returnedOn && <button type="button" disabled={busy} onClick={() => void perform(
                  () => api.post(`/employee-dossiers/${encodeURIComponent(appUserId)}/issued-items/${item.id}/return`, {}),
                  'Повернення зафіксовано.',
                )}>Повернути</button>}</td>
              </tr>)}
              {!record.issuedItems.length && <tr><td colSpan={7}>Записів немає.</td></tr>}
            </tbody></table></div>
          </section>

          <section className="employee-dossier__card">
            <h4>Табелювання та зарплата за {monthText}.{yearText}</h4>
            <p>Відпрацьовано: <strong>{record.attendanceHours.toFixed(2)} год</strong> · Метод: {record.salary.method === 'fixed-plus-revenue' ? 'ставка + % від виторгу' : 'коефіцієнт / розподіл підрозділу'} · Коефіцієнт: {record.salary.evaluationCoefficient?.toFixed(4) ?? record.salary.evaluationIssue ?? '—'} · Розрахункова сума: {money(record.salary.estimate)}</p>
            {record.salary.hallsPayroll && <p>Весільні Зали: {record.salary.hallsPayroll.status}, нараховано {money(record.salary.hallsPayroll.amount)}.</p>}
            {record.salary.monthlyPayroll && <p>Загальний зарплатний період: {record.salary.monthlyPayroll.status}, нараховано {money(record.salary.monthlyPayroll.amount)}.</p>}
            <div className="employee-dossier__table-wrap"><table><thead><tr><th>Дата</th><th>Прихід</th><th>Вихід</th><th>Години</th><th>Коеф.</th><th>Примітка</th></tr></thead><tbody>
              {record.attendance.map((entry, index) => <tr key={`${entry.workDate}-${entry.checkInAt}-${index}`}><td>{dateLabel(entry.workDate)}</td><td>{entry.checkInAt.slice(11, 16)}</td><td>{entry.checkOutAt?.slice(11, 16) || '—'}</td><td>{entry.hoursWorked.toFixed(2)}</td><td>{entry.hourCoefficient.toFixed(2)}</td><td>{entry.comment || '—'}</td></tr>)}
              {!record.attendance.length && <tr><td colSpan={6}>Записів за обраний місяць немає.</td></tr>}
            </tbody></table></div>
          </section>

          <button type="button" className="employee-dossier__print" onClick={printDossier}>Друк / Зберегти досьє у PDF</button>
        </>
      )}
    </section>
  );
};

export default EmployeeDossierPanel;
